import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import { ProductsApiService } from '../../../service/website/productsApiService';
import SEO from '../../../component/common/SEO';
import { pageSEO } from '../../../config/seoConfig';

import HomeBanner from '../../component/views/home/Banner';
// import SelectedProducts from '../../component/views/home/selectedProducts'; // REMOVED — not displayed
import DropShipSteps from '../../component/views/home/dropshipWorkSteps';
// import WhyUs from '../../component/views/home/whyUs';
// import ChannelPartners from '../../component/views/home/channelPartners';
import AiComponent from '../../component/views/home/aicomponent';
import Blogs from '../../component/views/home/blogs';
import InstagramVideo from '../../component/views/home/instagramVideo';
import FeaturedCategoris from '../../component/views/home/featuredCategoris';
import ClientsSection from '../../component/views/home/ourClients';
import TrustComponent from '../../component/views/home/trustcomponent';
import HomeCategory from '../../component/views/home/homeCategory';
import FeaturedCategoriesLanding from '../../component/views/home/FeaturedCategoriesLanding';
// import HeroSection from '../../component/views/home/Banner/FullWidthBanner';
import './style.css';
import HomeProductSkeletonRow from './HomeProductSkeleton';

export default function HomePage() {
  /** Sections (titles + metadata) — loaded first, so we can render section headers + skeletons immediately */
  const [sections, setSections] = useState([]);
  /** Products per section: { [fcId]: products[] } — each section fills in when its request completes */
  const [sectionProducts, setSectionProducts] = useState({});

  const message = useMessage();

  // Load sections first (fast) — then UI shows section titles and skeletons right away
  const fetchSections = useCallback(() => {
    ProductsApiService.getHomeProductSections()
      .then((res) => {
        if (res?.status?.code === 200 && Array.isArray(res.data)) {
          setSections(res.data);
        } else {
          message.showToast(res?.error?.message || 'Failed to load sections', ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
      });
  }, [message]);

  // Sections for top rows (exclude a few names)
  const orderedSectionsForFetch = useMemo(() => {
    if (!sections.length) return [];
    return sections
      .filter(
        (s) =>
          s.name &&
          s.name !== 'Home Product' &&
          s.name !== 'High Profit' &&
          s.name !== 'New Arrival' &&
          s.name !== 'Blow Out Sale'
      )
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  }, [sections]);

  // All section fcIds we need products for: top rows + lower block (showOnLandingPage), in display order
  const allSectionFcIdsToFetch = useMemo(() => {
    const fromTop = orderedSectionsForFetch.map((s) => s.fcId).filter(Boolean);
    const fromLanding = sections
      .filter((c) => c.showOnLandingPage === true)
      .sort((a, b) => (a.landingPageOrder ?? a.displayOrder ?? 0) - (b.landingPageOrder ?? b.displayOrder ?? 0))
      .map((c) => c.fcId)
      .filter(Boolean);
    const seen = new Set();
    const merged = [];
    for (const id of fromTop) {
      if (!seen.has(id)) {
        seen.add(id);
        merged.push(id);
      }
    }
    for (const id of fromLanding) {
      if (!seen.has(id)) {
        seen.add(id);
        merged.push(id);
      }
    }
    return merged;
  }, [sections, orderedSectionsForFetch]);

  // ⚡ Load section products in parallel batches of 3 (much faster than sequential)
  useEffect(() => {
    if (allSectionFcIdsToFetch.length === 0) return;
    let cancelled = false;
    const BATCH_SIZE = 3;
    const loadInBatches = async () => {
      for (let i = 0; i < allSectionFcIdsToFetch.length; i += BATCH_SIZE) {
        if (cancelled) return;
        const batch = allSectionFcIdsToFetch.slice(i, i + BATCH_SIZE);
        const results = await Promise.allSettled(
          batch.map((fcId) => ProductsApiService.getHomeProductSectionProducts(fcId).then((res) => ({ fcId, res })))
        );
        if (cancelled) return;
        setSectionProducts((prev) => {
          const next = { ...prev };
          for (const r of results) {
            if (r.status === 'fulfilled') {
              const { fcId, res } = r.value;
              next[fcId] = res?.status?.code === 200 ? res.data?.products ?? [] : [];
            } else if (r.status === 'rejected') {
              // batch item failed — leave empty
            }
          }
          return next;
        });
      }
    };
    loadInBatches();
    return () => {
      cancelled = true;
    };
  }, [allSectionFcIdsToFetch]);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  const filteredSections = useMemo(() => {
    if (!sections.length) return [];
    return sections
      .filter(
        (s) =>
          s.name &&
          s.name !== 'Home Product' &&
          s.name !== 'High Profit' &&
          s.name !== 'New Arrival' &&
          s.name !== 'Blow Out Sale'
      )
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  }, [sections]);

  // Sections that have products loaded and at least one product — render only these (no "No products" state)
  const sectionsWithProducts = useMemo(() => {
    return filteredSections.filter((section) => {
      const products = sectionProducts[section.fcId];
      return Array.isArray(products) && products.length > 0;
    });
  }, [filteredSections, sectionProducts]);

  // Still loading more sections (show one skeleton for "next" section)
  const stillLoadingSections = filteredSections.some((s) => sectionProducts[s.fcId] === undefined);

  // Only include lower-block categories that have products (no "No products" state)
  const landingPageCategories = useMemo(() => {
    if (!sections.length) return [];
    return sections
      .filter((c) => c.showOnLandingPage === true)
      .sort((a, b) => (a.landingPageOrder ?? a.displayOrder ?? 0) - (b.landingPageOrder ?? b.displayOrder ?? 0))
      .map((c) => ({ ...c, products: sectionProducts[c.fcId] ?? [] }))
      .filter((c) => (c.products || []).length > 0);
  }, [sections, sectionProducts]);

  return (
    <div>
      <SEO {...pageSEO.home} />
      {/* Render section only when its products are loaded — title + products together; never show "No products" */}
      {filteredSections.length === 0 ? (
        <HomeProductSkeletonRow title="Loading..." />
      ) : (
        <>
          {sectionsWithProducts.map((section) => (
            <FeaturedCategoris
              key={section.fcId || section.name}
              category={{ ...section, products: sectionProducts[section.fcId] }}
            />
          ))}
          {stillLoadingSections && <HomeProductSkeletonRow title="Loading..." />}
        </>
      )}

      <HomeCategory />
      <DropShipSteps headShow={true} />
      {sections.length > 0 && landingPageCategories.length > 0 && (
        <FeaturedCategoriesLanding categories={landingPageCategories} />
      )}
      <TrustComponent />
      {/* <WhyUs /> */}
      {/* <ClientsSection /> */}
      <AiComponent />
      {/* <ChannelPartners /> */}
      <Blogs />
    </div>
  );
}
